import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { LakshLogo } from '@/components/LakshLogo';
import axios from 'axios';
import { FeedbackDialog } from '@/components/FeedbackDialog';
import {
  Users,
  LayoutDashboard,
  Calendar,
  IndianRupee,
  Briefcase,
  LogOut,
  TrendingUp,
  Percent
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const formatCurrency = (num) => {
  if (!num) return '₹0';
  if (num >= 10000000) return `₹${(num/10000000).toFixed(2)} Cr`;
  if (num >= 100000) return `₹${(num/100000).toFixed(2)} L`;
  return `₹${num.toLocaleString('en-IN')}`;
};

const navItems = [
  { icon: LayoutDashboard, label: 'Dashboard', path: '/ca/dashboard' },
  { icon: Users, label: 'Clients', path: '/ca/clients' },
  { icon: Calendar, label: 'Compliance', path: '/ca/compliance' },
  { icon: IndianRupee, label: 'Revenue', path: '/ca/revenue' },
];

export default function CARevenuePage() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [firm, setFirm] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('laksh_ca_token');
    const userData = localStorage.getItem('laksh_ca_user');
    const firmData = localStorage.getItem('laksh_ca_firm');
    
    if (!token) {
      navigate('/ca/login');
      return;
    }
    
    if (userData) setUser(JSON.parse(userData));
    if (firmData) setFirm(JSON.parse(firmData));
    
    fetchRevenue(token);
  }, [navigate]);

  const fetchRevenue = async (token) => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/ca/revenue`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setData(response.data);
    } catch (error) {
      console.error('Revenue error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('laksh_ca_token');
    localStorage.removeItem('laksh_ca_user');
    localStorage.removeItem('laksh_ca_firm');
    navigate('/ca/login');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#06100A] flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-[#1D9E75] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#06100A]">
      {/* Sidebar */}
      <aside className="fixed left-0 top-0 h-full w-64 bg-[#0a1a12] border-r border-white/10 flex flex-col">
        <div className="p-4 border-b border-white/10">
          <LakshLogo height={32} />
          <div className="mt-2 flex items-center gap-2">
            <Briefcase className="w-4 h-4 text-[#1D9E75]" />
            <span className="text-xs text-[#1D9E75] font-medium">CA Portal</span>
          </div>
        </div>
        
        <nav className="flex-1 p-4 space-y-1">
          {navItems.map((item) => (
            <Link
              key={item.path}
              data-testid={`ca-nav-${item.label.toLowerCase()}`}
              to={item.path}
              className={`flex items-center gap-3 px-4 py-2.5 rounded-md text-gray-400 transition-colors hover:bg-white/5 hover:text-white ${
                item.path === '/ca/revenue' ? 'bg-[#1D9E75]/20 text-[#1D9E75]' : ''
              }`}
            >
              <item.icon className="w-5 h-5" strokeWidth={1.5} />
              <span>{item.label}</span>
            </Link>
          ))}
        </nav>

        <div className="p-4 border-t border-white/10">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-[#1D9E75]/20 rounded-full flex items-center justify-center">
              <span className="text-[#1D9E75] font-semibold">{user?.name?.charAt(0) || 'C'}</span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white truncate">{user?.name}</p>
              <p className="text-xs text-gray-500 truncate">{firm?.name}</p>
            </div>
          </div>
          <FeedbackDialog portal="ca" token={localStorage.getItem('laksh_ca_token')} triggerClassName="w-full px-4 py-2 mb-2 rounded-md text-gray-400 hover:text-white hover:bg-white/5" label="Send feedback" />
          <Button data-testid="ca-logout-btn" variant="ghost" onClick={handleLogout} className="w-full text-gray-400 hover:text-white hover:bg-white/5">
            <LogOut className="w-4 h-4 mr-2" /> Logout
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="ml-64 p-8">
        <div className="max-w-6xl mx-auto space-y-6">
          {/* Header */}
          <div>
            <h1 className="text-2xl font-bold text-white font-['Outfit']">Revenue Dashboard</h1>
            <p className="text-gray-400">Track your earnings from the CA Partner Programme</p>
          </div>

          {/* Revenue Summary */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card className="bg-gradient-to-br from-[#EF9F27]/20 to-[#EF9F27]/5 border-[#EF9F27]/30">
              <CardContent className="pt-6">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm text-[#EF9F27]/80">Monthly Revenue Share</p>
                    <p className="text-4xl font-bold text-[#EF9F27] font-mono">{formatCurrency(data?.total_revenue_share || 0)}</p>
                    <p className="text-xs text-gray-500 mt-1">{data?.revenue_share_pct || 20}% of {formatCurrency(data?.total_mrr || 0)} MRR</p>
                  </div>
                  <div className="w-14 h-14 bg-[#EF9F27]/20 rounded-xl flex items-center justify-center">
                    <IndianRupee className="w-7 h-7 text-[#EF9F27]" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-[#0a1a12] border-white/10">
              <CardContent className="pt-6">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm text-gray-400">Lifetime Earnings</p>
                    <p className="text-4xl font-bold text-white font-mono">{formatCurrency(data?.lifetime_earnings || 0)}</p>
                    <p className="text-xs text-gray-500 mt-1">Since joining</p>
                  </div>
                  <div className="w-14 h-14 bg-[#1D9E75]/20 rounded-xl flex items-center justify-center">
                    <TrendingUp className="w-7 h-7 text-[#1D9E75]" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-[#0a1a12] border-white/10">
              <CardContent className="pt-6">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm text-gray-400">Revenue Share Rate</p>
                    <p className="text-4xl font-bold text-white font-mono">{data?.revenue_share_pct || 20}%</p>
                    <p className="text-xs text-gray-500 mt-1">Of client subscriptions</p>
                  </div>
                  <div className="w-14 h-14 bg-blue-500/20 rounded-xl flex items-center justify-center">
                    <Percent className="w-7 h-7 text-blue-400" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Revenue by Client */}
          <Card className="bg-[#0a1a12] border-white/10">
            <CardHeader>
              <CardTitle className="text-white">Revenue by Client</CardTitle>
            </CardHeader>
            <CardContent>
              {data?.revenue_by_client?.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow className="border-white/10 hover:bg-transparent">
                      <TableHead className="text-gray-400">Business</TableHead>
                      <TableHead className="text-gray-400">Plan</TableHead>
                      <TableHead className="text-gray-400 text-right">Client MRR</TableHead>
                      <TableHead className="text-gray-400 text-right">Your Share</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.revenue_by_client.map((client, index) => (
                      <TableRow key={index} className="border-white/10 hover:bg-white/5">
                        <TableCell className="text-white font-medium">{client.business_name}</TableCell>
                        <TableCell>
                          <span className={`px-2 py-1 rounded text-xs font-medium ${
                            client.plan === 'scale' ? 'bg-[#EF9F27]/20 text-[#EF9F27]' :
                            client.plan === 'growth' ? 'bg-blue-500/20 text-blue-400' :
                            client.plan === 'starter' ? 'bg-green-500/20 text-green-400' :
                            'bg-gray-500/20 text-gray-400'
                          }`}>
                            {client.plan}
                          </span>
                        </TableCell>
                        <TableCell className="text-right font-mono text-white">{formatCurrency(client.mrr)}</TableCell>
                        <TableCell className="text-right font-mono text-[#EF9F27] font-semibold">
                          {formatCurrency(client.revenue_share)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <div className="text-center py-12">
                  <Users className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-white mb-2">No paying clients yet</h3>
                  <p className="text-gray-400">Invite clients and help them upgrade to start earning</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* How Revenue Share Works */}
          <Card className="bg-[#1D9E75]/10 border-[#1D9E75]/30">
            <CardContent className="pt-6">
              <h3 className="text-lg font-semibold text-white mb-4">How Revenue Share Works</h3>
              <div className="grid md:grid-cols-3 gap-6 text-sm">
                <div>
                  <p className="text-[#1D9E75] font-semibold mb-1">1. Client Subscribes</p>
                  <p className="text-gray-400">When your referred client upgrades to a paid plan</p>
                </div>
                <div>
                  <p className="text-[#1D9E75] font-semibold mb-1">2. You Earn 20%</p>
                  <p className="text-gray-400">Of their monthly subscription, every month</p>
                </div>
                <div>
                  <p className="text-[#1D9E75] font-semibold mb-1">3. Lifetime Revenue</p>
                  <p className="text-gray-400">As long as they stay subscribed, you earn</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
}
